import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle, ExternalLink, Loader2 } from 'lucide-react';
import { businessesService } from '@services/businesses.service';
import { usePermission } from '@hooks/usePermission';
import { cn } from '@lib/utils';

const fmt = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' });
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">{label}</div>
      <div className="text-[13px] text-[var(--text-primary)]">{value}</div>
    </div>
  );
}

/** Read-only "Setup progress" for reviewers. Backend: GET /admin/businesses/{id}/onboarding (businesses.read). */
export const BusinessOnboardingPanel = ({ businessId }: { businessId: string }) => {
  const { hasCheKitPermission } = usePermission();
  const allowed = hasCheKitPermission('businesses.read');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['business-onboarding', businessId],
    queryFn: () => businessesService.getOnboarding(businessId),
    enabled: allowed,
  });

  if (!allowed) return null;

  return (
    <div className="rounded-3xl bg-white shadow-soft p-6">
      <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-4">Setup progress</h3>

      {isLoading && (
        <div className="flex items-center gap-2 text-[14px] text-[var(--text-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Loading…
        </div>
      )}
      {isError && <p className="text-[13px] text-[var(--text-muted)]">Could not load setup progress.</p>}

      {data && (() => {
        const sig = data.signals ?? {};
        const eng = data.engagement ?? {};
        const hasEngagement = Object.keys(eng).length > 0;
        const cats = sig.selectedCategories ?? [];
        const pct = Math.max(0, Math.min(100, Number(data.progressPct) || 0));
        return (
          <div className="space-y-6">
            <div>
              <div className="mb-1.5 flex items-center justify-between text-[13px]">
                <span className="text-[var(--text-secondary)]">
                  {data.requiredDone} of {data.requiredTotal} required steps done
                </span>
                <span className="font-semibold text-[var(--text-primary)]">{Math.round(pct)}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-[var(--bg-base)]">
                <div className="h-full rounded-full bg-[var(--state-success)]" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <ul className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {data.steps.map((s) => (
                <li key={s.key} className={cn('flex items-start gap-2', s.optional && !s.done && 'opacity-60')}>
                  {s.done ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-success)]" strokeWidth={1.5} />
                  ) : (
                    <Circle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-muted)]" strokeWidth={1.5} />
                  )}
                  <div>
                    <div className="text-[14px] font-medium text-[var(--text-primary)]">
                      {s.title}
                      {s.optional && <span className="ml-2 text-[11px] font-normal text-[var(--text-muted)]">optional</span>}
                    </div>
                    <div className="text-[12px] text-[var(--text-muted)]">{s.description}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-1 gap-6 border-t border-[var(--border-subtle)] pt-5 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-[13px] font-semibold text-[var(--text-secondary)]">What they told us</p>
                <div className="space-y-1.5 text-[13px] text-[var(--text-primary)]">
                  <p>
                    Categories picked: {cats.length}
                    {cats.length > 0 && <span className="text-[var(--text-muted)]"> ({cats.join(', ')})</span>}
                  </p>
                  <p>
                    Category satisfaction:{' '}
                    {sig.categorySatisfaction != null ? `${sig.categorySatisfaction}/5` : <span className="text-[var(--text-muted)]">not rated</span>}
                  </p>
                  {sig.categoryFeedback && (
                    <p className="rounded-xl bg-[var(--bg-base)] p-3 text-[var(--text-secondary)]">{sig.categoryFeedback}</p>
                  )}
                  <p>
                    Earnings calculator:{' '}
                    {sig.calculatorUsedAt ? `used ${fmt(sig.calculatorUsedAt)}` : <span className="text-[var(--text-muted)]">not used</span>}
                  </p>
                </div>
              </div>

              <div>
                <p className="mb-2 text-[13px] font-semibold text-[var(--text-secondary)]">Engagement</p>
                {hasEngagement ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Stat label="First seen" value={fmt(eng.first_seen_at)} />
                    <Stat label="Last activity" value={fmt(eng.last_activity_at)} />
                    <Stat label="Activated" value={fmt(eng.activated_at)} />
                    <Stat label="Reminders sent" value={String(eng.nudge_count ?? 0)} />
                  </div>
                ) : (
                  <p className="text-[13px] text-[var(--text-muted)]">Hasn&apos;t opened the portal yet.</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-[var(--border-subtle)] pt-4 text-[13px]">
              <span className="text-[var(--text-secondary)]">Go-live:</span>
              <span
                className={cn(
                  'rounded-full px-2.5 py-1 text-[12px] font-semibold',
                  data.goLive.live
                    ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                    : 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                )}
              >
                {data.goLive.live ? 'Live' : 'Not live'} · {data.goLive.status.replace(/_/g, ' ')}
              </span>
              {data.goLive.live && data.goLive.shopUrl && (
                <a href={data.goLive.shopUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--text-primary)] hover:underline">
                  {data.goLive.shopUrl} <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {data.dismissed && <span className="text-[12px] text-[var(--text-muted)]">Checklist dismissed by merchant</span>}
            </div>
          </div>
        );
      })()}
    </div>
  );
};
